/**
 * 全域攔掉「media play() 被中斷」這一種良性的 unhandled rejection。
 *
 * 情境：掃描頁（ScanViewport / html5-qrcode）在相機啟動中被拆卸時，內部
 * `video.play()` 的 promise 會 reject——
 *   "The play() request was interrupted because the media was removed from the document"
 * 這是正常的拆卸競態，掃描器本身沒壞，使用者也沒有任何損失。但它不在
 * html5-qrcode `start()` 的 await 範圍內，會變成 unhandledrejection：
 * dev 下被 CRA 的 error overlay 攔成紅屏，prod 則被 Sentry 記成錯誤。
 *
 * 為什麼必須「常駐」而不能綁在元件裡：ScanViewport 原本在自己的 useEffect
 * 註冊這個攔截器，但 React 卸載時是**先跑 cleanup 移除 listener、再移除 DOM**，
 * 而 play() 的 reject 發生在那之後的 microtask——正好落在沒有 listener 的空窗，
 * 所以照樣紅屏。註冊一次、不移除，才接得到。
 *
 * 只攔訊息明確吻合的那一種，其他 rejection 一律照常拋出，不掩蓋真的錯誤。
 */
const isBenignMediaPlayError = (reason: unknown): boolean => {
  const msg = String((reason && ((reason as Error).message || reason)) || "").toLowerCase();
  if (!msg) return false;
  return (msg.includes("play()") && msg.includes("interrupted")) || msg.includes("media was removed from the document");
};

let installed = false;

/** 在 app 進入點呼叫一次。重複呼叫無效果。 */
export const installMediaPlayRejectionSuppressor = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    if (isBenignMediaPlayError(e.reason)) e.preventDefault();
  });

  // 某些瀏覽器 / CRA overlay 會把它包成 error 事件而非 rejection，兩邊都要接。
  window.addEventListener("error", (e: ErrorEvent) => {
    if (isBenignMediaPlayError(e.error || e.message)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  });
};
